import React from "react";
import { useHistory } from "react-router-dom";
import TypeShowcase from "../../../components/landing/TypeShowcase";
import type { ListingSubcategory } from "../../../components/landing/ListingGrid";

type WorkspaceType = {
  key: string;
  name: string;
  // Fallback subcategory ID used by /workspaces-search (carried over from the previous page)
  subCategoryId: number;
  // Registry names this type can appear under; the live ID wins when one matches
  aliases: string[];
  color: string;
  background: string;
  image: string;
  imageSide: "left" | "right";
  // In the design the gradient only shows where it sits above the photo
  gradient: "none" | "strong";
  description: string;
  included: string[];
  whyItWorks: string[];
  cta: string;
};

const WORKSPACE_TYPES: WorkspaceType[] = [
  {
    key: "coworking",
    name: "Coworking Spaces",
    subCategoryId: 6,
    aliases: ["co-working space", "coworking space", "coworking"],
    color: "#017bef",
    background: "bg-white",
    image: "/figma-assets/work/coworking.jpg",
    imageSide: "left",
    gradient: "none",
    description: "Access shared, professional workspaces designed for productivity and connection.",
    included: [
      "Flexible hot-desk access",
      "Daily, weekly and monthly passes",
      "High-speed internet and business amenities",
      "Community and networking events",
      "Comfortable collaborative environments",
      "Real-time availability",
    ],
    whyItWorks: [
      "Work productively, wherever you are",
      "Reduce the cost of a permanent office",
      "Connect with a wider professional community",
      "Business-ready environments for focus and collaboration",
    ],
    cta: "Explore Coworking Spaces",
  },
  {
    key: "dedicated-desks",
    name: "Dedicated Desks",
    subCategoryId: 5,
    aliases: ["dedicated desk", "dedicated desks", "hot desk"],
    color: "#0062c0",
    background: "bg-[#f8fbff]",
    image: "/figma-assets/work/dedicated-desks.jpg",
    imageSide: "right",
    gradient: "none",
    description: "A reserved desk of your own, in a space built for consistency and focus.",
    included: [
      "Personal, reserved workspace",
      "Secure and comfortable working environment",
      "Flexible booking times",
      "Access to shared facilities and amenities",
      "Consistent working environment",
    ],
    whyItWorks: [
      "A familiar space to call your own",
      "Cost-effective alternative to a private office",
      "Greater routine and focus",
      "The collaborative atmosphere of coworking with your own dedicated space",
    ],
    cta: "Explore Dedicated Desks",
  },
  {
    key: "meeting-rooms",
    name: "Meeting Rooms",
    subCategoryId: 4,
    aliases: ["meeting room", "meeting rooms"],
    color: "#017bef",
    background: "bg-white",
    image: "/figma-assets/work/meeting-rooms.jpg",
    imageSide: "left",
    gradient: "strong",
    description: "Professional, bookable spaces for focused conversations and client meetings.",
    included: [
      "Flexible booking options",
      "Video conferencing and presentation equipment",
      "Adequate room sizes",
      "Whiteboards and presentation tools",
      "Convenient business locations",
      "High-speed internet",
    ],
    whyItWorks: [
      "A professional setting for every meeting",
      "Pay only for the time you need",
      "No long-term commitment required",
      "Comfortable and fully equipped environments",
    ],
    cta: "Find a Meeting Room",
  },
  {
    key: "conference-rooms",
    name: "Conference Rooms",
    // No dedicated conference-room subcategory yet; the previous page also used the meeting-room ID
    subCategoryId: 4,
    aliases: ["conference room", "conference rooms"],
    color: "#0062c0",
    background: "bg-[#f8fbff]",
    image: "/figma-assets/work/conference-rooms.jpg",
    imageSide: "right",
    gradient: "strong",
    description: "Larger spaces for team gatherings, training sessions and corporate events.",
    included: [
      "Larger seating capacity",
      "Full AV and presentation equipment",
      "Flexible room layouts",
      "Catering options where available",
      "On-site support",
      "High-speed internet",
    ],
    whyItWorks: [
      "Host professional events without permanent facilities",
      "Improve participant experience through quality venues",
      "Easily scale your meeting space based on attendance",
    ],
    cta: "Explore Conference Rooms",
  },
  {
    key: "private-offices",
    name: "Private Offices",
    subCategoryId: 1,
    aliases: ["private office", "private offices"],
    color: "#012d6e",
    background: "bg-white",
    image: "/figma-assets/work/private-offices.jpg",
    imageSide: "left",
    gradient: "strong",
    description: "Fully enclosed, secure office space for teams that need privacy and permanence.",
    included: [
      "Fully furnished private offices",
      "Flexible lease options",
      "Access to meeting rooms and shared amenities",
      "Reception and business support services",
      "Scalable to team size",
    ],
    whyItWorks: [
      "Establish a professional business presence",
      "Privacy, security and a professional address",
      "Room to grow as your team expands",
      "All shared amenities with the privacy of your own office",
      "Reduce the burden of long-term office leases",
    ],
    cta: "Explore Private Offices",
  },
];

/** Prefer the live registry ID for this type, falling back to the ID the previous page used. */
const searchUrl = (type: WorkspaceType, subcategories: ListingSubcategory[]) => {
  const match = subcategories.find(sub => type.aliases.includes(String(sub.name || "").toLowerCase()));
  return `/workspaces-search?type=work&categoryId=1&subCategoryId=${match ? match.id : type.subCategoryId}`;
};

const availabilityStrip = (
  <div className="flex items-center justify-between">
    <span className="flex items-center gap-2 text-[12px] font-semibold leading-4 text-[#0a0a0a]">
      <span className="size-2 rounded-full bg-[#22c55e]" />
      Available now
    </span>
    <span className="text-[12px] leading-4 text-[#717182]">Book instantly</span>
  </div>
);

type WorkspaceTypesProps = { subcategories: ListingSubcategory[] };

export default function WorkspaceTypes({ subcategories }: WorkspaceTypesProps) {
  const history = useHistory();

  return (
    <>
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1440px] px-[16px] sm:px-8">
          <div className="grid grid-cols-1 items-end gap-6 lg:grid-cols-2 lg:gap-12">
            <div>
              <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">
                Workspace Types
              </p>
              <h2 className="pt-[12px] text-[36px] font-bold leading-[1.2] tracking-[-1.2px] text-[#0a0a0a] sm:text-[48px] sm:leading-[48px]">
                Find the Right Space to Work
              </h2>
            </div>
            <p className="text-[18px] leading-[29.25px] text-[#717182]">
              From coworking spaces and dedicated desks to meeting rooms, conference rooms and private offices, SPACIA
              gives individuals and teams flexible access to professional workspaces, wherever they need them.
            </p>
          </div>

          <div className="flex flex-wrap gap-[12px] pt-10">
            {WORKSPACE_TYPES.map(type => (
              <button
                key={type.key}
                type="button"
                onClick={() => history.push(searchUrl(type, subcategories))}
                className="rounded-full border-[1px] border-solid border-[#e5e7eb] px-[20px] py-2.5 text-[14px] font-semibold leading-5 text-[#0a0a0a] transition-colors hover:border-[#017bef]/30 hover:bg-[#017bef]/[0.04] hover:text-[#017bef]"
              >
                {type.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {WORKSPACE_TYPES.map((type, index) => (
        <TypeShowcase
          key={type.key}
          eyebrow="SPACIA WORK"
          name={type.name}
          color={type.color}
          background={type.background}
          image={type.image}
          imageSide={type.imageSide}
          gradient={type.gradient}
          imageFooter={availabilityStrip}
          description={type.description}
          included={type.included}
          whyItWorks={type.whyItWorks}
          cta={type.cta}
          onCta={() => history.push(searchUrl(type, subcategories))}
          // The first block sits closer to the intro in the design
          className={index === 0 ? "pb-20 pt-[31px]" : "py-20"}
        />
      ))}
    </>
  );
}
